"use client";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { cn, extractDate, htParse } from "@/lib/utils";
import { OptionsContext } from "@/provider/options-provider";
import { useContext, useState } from "react";

import { Rating } from "@/components/rating";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { formatHit } from "@/provider/ai/tools";
import { AiAskRequest, AiAskResponse } from "@/provider/ai/type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { HiOutlineChatBubbleOvalLeftEllipsis } from "react-icons/hi2";
import { v4 as uuid } from "uuid";
import { z } from "zod";

const FormSchema = z.object({
  system_prompt: z.string().min(2, {
    message: "System behavior must be at least 2 characters.",
  }),
  prompt: z.string().min(2, {
    message: "You must type in a question first.",
  }),
});

const defaults = {
  system_prompt:
    "You will act as an advisor to a financial portfolio manager who invests in global equity markets. When I ask to summarize something, you will reply with key takeaways in dot points. Use numbers where applicable.",
  prompt:
    "You are given the following list of extracts from multiple documents in the form of document title followed by the segment. Consolidate and summarize :",
};

export function SummarySheet() {
  const {
    searchResults,
    groupSummaryResult,
    setGroupSummaryResult,
    searchOptions,
    setSearchOptions,
    groupSummaryItems,
    searchText,
  } = useContext(OptionsContext);
  const [answer, setAnswer] = useState<string | undefined>();
  const [format, setFormat] = useState<"qa" | "summarize">("qa");
  const [loading, setLoading] = useState(false);
  const [summarize, setSummarize] = useState(false);
  const [hint, setHint] = useState<string | undefined>();
  const [model, setModel] = useState<string | undefined>();
  const [useSummary, setUseSummary] = useState(false);
  const [feedbackId, setFeedbackId] = useState<string | undefined>();

  const inputText =
    searchResults
      ?.filter((i) => groupSummaryItems.find((g) => g.id === i.id))
      ?.map((r) => formatHit(r))
      .join("\n") ?? "";

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      system_prompt: searchOptions.systemPrompt || "",
      prompt: searchOptions.prompt || "",
    },
    values: {
      system_prompt: searchOptions.systemPrompt || "",
      prompt: searchOptions.prompt || "",
    },
  });

  async function ask() {
    setAnswer(undefined);
    setHint(undefined);
    setModel(undefined);
    setLoading(true);
    const req: AiAskRequest = {
      prompt: form.getValues("prompt"),
      system_prompt: form.getValues("system_prompt"),
      input_text: inputText,
      event_type: "Q_AND_A_SEARCH",
      keywords: [searchText],
      ticker: searchOptions.ticker,
      period: `${extractDate(searchOptions.dateRange?.from)} to ${extractDate(
        searchOptions.dateRange?.to,
      )}`,
    };

    const response = await fetch("/api/ask", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(req),
      cache:
        form.getValues("prompt") === defaults.prompt
          ? "force-cache"
          : "default",
    });
    const result: AiAskResponse = await response.json();
    setAnswer(result.generated_summary);
    setHint(result.hint);
    setModel(result.model);
    setFeedbackId(result.id ?? uuid());
    setLoading(false);
  }

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    await ask();
  }

  return (
    <div className="flex py-5">
      <Sheet
        open={groupSummaryResult !== undefined}
        onOpenChange={() => {
          setGroupSummaryResult(undefined);
          form.setValue("prompt", "");
          form.setValue("system_prompt", searchOptions.systemPrompt);
          setAnswer(undefined);
          setHint(undefined);
          setModel(undefined);
          setSummarize(false);
          setFormat("qa");
        }}
      >
        <SheetContent className="w-[400px] overflow-y-scroll bg-yellow-100 lg:min-w-[700px]">
          {
            <Accordion
              type="multiple"
              defaultValue={[searchOptions.showGroupSummary ? "" : "item-1"]}
            >
              <AccordionItem value="item-1" className="mt-3">
                <AccordionTrigger disabled={!searchOptions.showGroupSummary}>
                  Ask the results?
                </AccordionTrigger>
                <AccordionContent className="px-1">
                  <div className="mt-1">
                    <ToggleGroup
                      type="single"
                      defaultValue="qa"
                      value={format}
                      onValueChange={async (value) => {
                        setFormat(value as "qa" | "summarize");
                        if (value === "summarize") {
                          form.setValue("prompt", defaults.prompt);
                          form.setValue(
                            "system_prompt",
                            defaults.system_prompt,
                          );
                          setAnswer(undefined);
                          setHint(undefined);
                          setModel(undefined);
                          setSummarize(true);
                          await ask();
                        } else {
                          form.setValue("prompt", "");
                          form.setValue(
                            "system_prompt",
                            searchOptions.systemPrompt,
                          );
                          setAnswer(undefined);
                          setHint(undefined);
                          setModel(undefined);
                          setSummarize(false);
                        }
                      }}
                    >
                      <ToggleGroupItem
                        value="summarize"
                        aria-label="Toggle summarize"
                      >
                        <Label className="">Summarize</Label>
                      </ToggleGroupItem>
                      <ToggleGroupItem value="qa" aria-label="Toggle q&a">
                        <Label className="">Q and A</Label>
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                  <div>
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="w-full space-y-6 pt-4"
                      >
                        <Collapsible open={!summarize}>
                          <CollapsibleTrigger></CollapsibleTrigger>
                          <CollapsibleContent>
                            <FormField
                              control={form.control}
                              name="prompt"
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <Textarea
                                      className="h-32"
                                      placeholder="Type a question and hit enter..."
                                      {...field}
                                      onKeyDown={(e) => {
                                        // Shift enter to new line and enter to submits
                                        if (e.key === "Enter" && !e.shiftKey) {
                                          e.preventDefault();
                                          form.handleSubmit(onSubmit)();
                                        }
                                      }}
                                      onChange={(e) => {
                                        form.setValue("prompt", e.target.value);
                                        setSearchOptions({
                                          ...searchOptions,
                                          prompt: e.target.value,
                                        });
                                      }}
                                      disabled={format === "summarize"}
                                    />
                                  </FormControl>
                                  <FormDescription className="text-xs">
                                    Ask a question relevant to the results.
                                    Press ⏎ to submit and ⇧ + ⏎ for new line.
                                  </FormDescription>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <Collapsible>
                              <CollapsibleTrigger>
                                <HiOutlineChatBubbleOvalLeftEllipsis />
                              </CollapsibleTrigger>
                              <CollapsibleContent>
                                <FormField
                                  control={form.control}
                                  name="system_prompt"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Textarea
                                          className="h-32"
                                          placeholder="describe the behavior of the system..."
                                          {...field}
                                          onKeyDown={(e) => {
                                            // Shift enter to new line and enter to submit
                                            if (
                                              e.key === "Enter" &&
                                              !e.shiftKey
                                            ) {
                                              e.preventDefault();
                                              form.handleSubmit(onSubmit)();
                                            }
                                          }}
                                          onChange={(e) => {
                                            form.setValue(
                                              "system_prompt",
                                              e.target.value,
                                            );
                                            setSearchOptions({
                                              ...searchOptions,
                                              systemPrompt: e.target.value,
                                            });
                                          }}
                                          disabled={format === "summarize"}
                                        />
                                      </FormControl>
                                      <FormDescription className="text-xs">
                                        Describes system behavior
                                      </FormDescription>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </CollapsibleContent>
                            </Collapsible>
                          </CollapsibleContent>
                        </Collapsible>
                        {format === "qa" && (
                          <div className="flex flex-row">
                            <Button
                              type="submit"
                              disabled={loading || form.formState.isSubmitting}
                            >
                              Ask
                            </Button>
                            <div className="flex flex-row items-center space-x-2">
                              <Switch
                                className="ml-2 scale-75"
                                checked={useSummary}
                                onCheckedChange={() =>
                                  setUseSummary(!useSummary)
                                }
                                disabled={loading}
                              ></Switch>
                              <Label className="text-xs text-muted-foreground">
                                Use summaries instead of first pages
                              </Label>
                            </div>
                          </div>
                        )}
                      </form>
                    </Form>
                  </div>
                  <div>
                    <pre className="mt-2 flex w-full flex-col rounded-md bg-slate-950 p-4">
                      {answer ? (
                        <code className="text-wrap text-white">
                          {htParse(answer || "")}
                        </code>
                      ) : (
                        <code
                          className={cn(
                            "pl-2 text-white",
                            loading ? "animate-ping" : "",
                          )}
                        >
                          ...
                        </code>
                      )}
                      {hint && (
                        <code className="mt-4 text-wrap text-xs text-gray-400">
                          {hint}
                        </code>
                      )}
                      {model && (
                        <code className="text-wrap text-xsm text-gray-400">
                          {model}
                        </code>
                      )}
                    </pre>

                    {feedbackId && (
                      <Rating
                        rating={0}
                        feedbackId={feedbackId}
                        setFeedbackId={setFeedbackId}
                        model={model}
                      />
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          }
          {searchOptions.showGroupSummary && (
            <>
              <h4>Group Summary</h4>
              <div className="p-3 text-justify text-sm">
                {htParse(groupSummaryResult || "")}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
